import { useEffect, useMemo, useState } from 'react';
import { Clock3, KeyRound, LocateFixed, Mail, Save, UserRound } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';
import { PageHeader, Surface } from '../components/ui';

export default function ProfilePage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [email, setEmail] = useState(user?.email || '');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [timezone, setTimezone] = useState('UTC');
  const [timezones, setTimezones] = useState(['UTC']);
  const [timezoneSaving, setTimezoneSaving] = useState(false);
  const [timezoneError, setTimezoneError] = useState('');
  const [timezoneSuccess, setTimezoneSuccess] = useState(false);
  const [emailPwd, setEmailPwd] = useState('');
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [curPwd, setCurPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdError, setPwdError] = useState('');
  const [pwdSuccess, setPwdSuccess] = useState(false);

  useEffect(() => {
    Promise.all([
      api.me(),
      api.getTimezones().catch(() => ['UTC']),
    ])
      .then(([data, available]) => {
        const currentTimezone = data.timezone || 'UTC';
        setEmail(data.email || '');
        setTimezone(currentTimezone);
        setTimezones(available.includes(currentTimezone) ? available : [currentTimezone, ...available]);
      })
      .catch((err) => setLoadError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const detectedTimezone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    [],
  );
  const timezonePreview = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(undefined, {
        dateStyle: 'long',
        timeStyle: 'medium',
        timeZone: timezone,
      }).format(new Date());
    } catch {
      return '';
    }
  }, [timezone]);

  const handleTimezoneSave = async (event) => {
    event.preventDefault();
    setTimezoneError(''); setTimezoneSuccess(false); setTimezoneSaving(true);
    try {
      const updated = await api.updateTimezone(timezone);
      setTimezone(updated.timezone);
      setTimezoneSuccess(true);
      setTimeout(() => setTimezoneSuccess(false), 3000);
    } catch (err) { setTimezoneError(err.message); }
    finally { setTimezoneSaving(false); }
  };

  const handleEmailSave = async (event) => {
    event.preventDefault();
    setEmailError(''); setEmailSuccess(false); setEmailSaving(true);
    try {
      await api.updateMe({ current_password: emailPwd, new_email: email });
      setEmailPwd(''); setEmailSuccess(true);
      setTimeout(() => setEmailSuccess(false), 3000);
    } catch (err) { setEmailError(err.message); }
    finally { setEmailSaving(false); }
  };

  const handlePwdSave = async (event) => {
    event.preventDefault();
    setPwdError(''); setPwdSuccess(false);
    if (newPwd !== confirmPwd) { setPwdError(t('profile.pwdMismatch')); return; }
    if (newPwd.length < 6) { setPwdError(t('profile.pwdTooShort')); return; }
    setPwdSaving(true);
    try {
      await api.updateMe({ current_password: curPwd, new_password: newPwd });
      setCurPwd(''); setNewPwd(''); setConfirmPwd(''); setPwdSuccess(true);
      setTimeout(() => setPwdSuccess(false), 3000);
    } catch (err) { setPwdError(err.message); }
    finally { setPwdSaving(false); }
  };

  return (
    <div className="page page-narrow">
      <PageHeader icon={UserRound} title={t('profile.title')} subtitle={user?.username} />
      {loadError && <div className="alert alert-danger" role="alert">{loadError}</div>}
      <Surface title={t('profile.timezoneTitle')} icon={Clock3} className="mb-4">
        <div className="surface-body">
          <form onSubmit={handleTimezoneSave}>
            <div className="mb-3">
              <label className="form-label" htmlFor="profile-timezone">{t('profile.timezoneLabel')}</label>
              <select id="profile-timezone" className="form-select" value={timezone} onChange={(event) => setTimezone(event.target.value)} disabled={loading}>
                {timezones.map((name) => <option key={name} value={name}>{name}</option>)}
              </select>
              <div className="form-text">{t('profile.timezoneHint')}</div>
            </div>
            {timezonePreview && <p className="small text-muted mb-3">{t('profile.timezoneCurrentTime', { time: timezonePreview })}</p>}
            {timezones.includes(detectedTimezone) && detectedTimezone !== timezone && (
              <button type="button" className="btn btn-outline-secondary mb-3" onClick={() => setTimezone(detectedTimezone)}>
                <LocateFixed className="icon" aria-hidden="true" />{t('profile.useDetectedTimezone', { timezone: detectedTimezone })}
              </button>
            )}
            {timezoneError && <div className="alert alert-danger py-2 small" role="alert">{timezoneError}</div>}
            {timezoneSuccess && <div className="alert alert-success py-2 small" role="status">{t('profile.saved')}</div>}
            <button type="submit" className="btn btn-primary" disabled={loading || timezoneSaving}>
              <Save className="icon" aria-hidden="true" />{timezoneSaving ? t('profile.saving') : t('profile.save')}
            </button>
          </form>
        </div>
      </Surface>
      <Surface title={t('profile.emailTitle')} icon={Mail} className="mb-4">
        <div className="surface-body">
          <form onSubmit={handleEmailSave}>
            <div className="mb-3"><label className="form-label" htmlFor="profile-email">{t('profile.newEmail')}</label><input id="profile-email" className="form-control" type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" disabled={loading} required /></div>
            <div className="mb-3"><label className="form-label" htmlFor="profile-email-password">{t('profile.currentPwdConfirm')}</label><input id="profile-email-password" className="form-control" type="password" value={emailPwd} onChange={(event) => setEmailPwd(event.target.value)} autoComplete="current-password" required /></div>
            {emailError && <div className="alert alert-danger py-2 small" role="alert">{emailError}</div>}
            {emailSuccess && <div className="alert alert-success py-2 small" role="status">{t('profile.saved')}</div>}
            <button type="submit" className="btn btn-primary" disabled={loading || emailSaving}><Save className="icon" aria-hidden="true" />{emailSaving ? t('profile.saving') : t('profile.save')}</button>
          </form>
        </div>
      </Surface>

      <Surface title={t('profile.pwdTitle')} icon={KeyRound}>
        <div className="surface-body">
          <form onSubmit={handlePwdSave}>
            <div className="mb-3"><label className="form-label" htmlFor="profile-current-password">{t('profile.currentPwd')}</label><input id="profile-current-password" className="form-control" type="password" value={curPwd} onChange={(event) => setCurPwd(event.target.value)} autoComplete="current-password" required /></div>
            <div className="mb-3"><label className="form-label" htmlFor="profile-new-password">{t('profile.newPwd')}</label><input id="profile-new-password" className="form-control" type="password" value={newPwd} onChange={(event) => setNewPwd(event.target.value)} autoComplete="new-password" required /></div>
            <div className="mb-3"><label className="form-label" htmlFor="profile-confirm-password">{t('profile.confirmPwd')}</label><input id="profile-confirm-password" className="form-control" type="password" value={confirmPwd} onChange={(event) => setConfirmPwd(event.target.value)} autoComplete="new-password" required /></div>
            {pwdError && <div className="alert alert-danger py-2 small" role="alert">{pwdError}</div>}
            {pwdSuccess && <div className="alert alert-success py-2 small" role="status">{t('profile.saved')}</div>}
            <button type="submit" className="btn btn-primary" disabled={pwdSaving}><Save className="icon" aria-hidden="true" />{pwdSaving ? t('profile.saving') : t('profile.save')}</button>
          </form>
        </div>
      </Surface>
    </div>
  );
}
